<script>
import { mapGetters } from 'vuex';

import { multicurrencyMixin } from '@/mixins/offices';

import TariffsDescriptions from './components/TariffsDescriptions';
import CommonTariffsTableColumn from './components/CommonTariffsTableColumn';

export default {
  name: 'tariffs-table',
  components: {
    TariffsDescriptions,
    CommonTariffsTableColumn,
    WhatsappPushTariffsTableColumn: () => import('./components/WhatsappPushTariffsTableColumn'),
    SmsViberTariffsTableColumn: () => import('./components/SmsViberTariffsTableColumn')
  },
  mixins: [multicurrencyMixin],
  props: {
    mau: Array,
    tariffs: Array,
    country: Number,
    channel: String,
    operator: String,
    countries: Array,
    operators: Array,
    isChannelSms: Boolean,
    subsciptionFee: Array,
    tariffsLoading: Boolean,
    isChannelViber: Boolean,
    isChannelWhatsapp: Boolean,
    isChannelEmail: Boolean,
    isChannelPush: Boolean
  },
  computed: {
    ...mapGetters({
      countryData: 'GET_COUNTRY_DATA'
    }),
    isTabsTransparent() {
      return this.operators.length == 1 && this.operators[0].label == '';
    },
    preparedTariffs() {
      if (this.isChannelPush) {
        return [...this.tariffs, ...this.mau];
      } else if (this.isChannelEmail) {
        return this.tariffs;
      }
      return [...this.tariffs, ...this.mau, ...[{ isEmptyCell: true }], ...this.subsciptionFee];
    },
    showCountrySelection() {
      return this.channel !== 'PUSH' && this.channel !== 'EMAIL';
    },
    showTariffsTable() {
      return this.country || this.channel === 'PUSH' || this.channel === 'EMAIL';
    }
  },
  methods: {
    getRowClassName({ row }) {
      const { steps, isEmptyCell } = row;

      if (steps && steps.length == 1) {
        return 'tariffs__table_hide-expand';
      } else if (!steps && !isEmptyCell) {
        return 'tariffs__table_fee';
      } else if (isEmptyCell) {
        return 'tariffs__table_empty-row';
      }
    },
    arraySpanMethod({ row, columnIndex }) {
      const { isFee } = row;

      if (isFee && !this.isChannelWhatsapp && !this.isChannelPush) {
        if (columnIndex == 0) {
          return [1, 3];
        } else if (columnIndex == 1 || columnIndex == 2) {
          return [0, 0];
        }
      }
    }
  }
};
</script>

<template>
  <div class="tariffs-table" v-loading="tariffsLoading">
    <el-select
      class="tariffs__select"
      filterable
      v-if="showCountrySelection"
      :value="country"
      :placeholder="$t('fe.choose.country') | capitalize"
      :no-data-text="$t('fe.no.country.found') | capitalize"
      :no-match-text="$t('fe.no.country.found') | capitalize"
      @change="$emit('change-country', $event)"
    >
      <el-option :key="value" :label="label" :value="value" v-for="{ value, label } in countries" />
    </el-select>

    <template v-if="showTariffsTable">
      <el-tabs
        type="card"
        tab-position="left"
        :value="operator"
        :class="['tariffs__tabs', { tariffs__tabs_transparent: isTabsTransparent }]"
        v-if="operators.length"
        @tab-click="$emit('change-operator', $event.name)"
      >
        <el-tab-pane
          lazy
          element-loading-background="#ffffff"
          :key="value"
          :name="String(value)"
          :label="label"
          v-for="{ label, value } in operators"
        >
          <div class="tariffs__data">
            <el-table
              class="tariffs__table"
              :data="preparedTariffs"
              :empty-text="$t('fe.tariffs.are.empty') | capitalize"
              :span-method="arraySpanMethod"
              :row-class-name="getRowClassName"
            >
              <template v-if="isChannelSms || isChannelViber">
                <el-table-column :label="$t('fe.subscribe.type') | capitalize">
                  <template #default="{ row }">
                    <template v-if="!row.isEmptyCell">
                      <sms-viber-tariffs-table-column
                        subsciptionFeeColumn
                        :isChannelViber="isChannelViber"
                        :planRecordId="row.planRecordId"
                        :sourceAddressType="row.sourceAddressType"
                      />
                    </template>
                  </template>
                </el-table-column>
                <el-table-column :label="$t('fe.messages.type') | capitalize">
                  <template #default="{ row }">
                    <template v-if="!row.isEmptyCell">
                      <sms-viber-tariffs-table-column :traffic="row.traffic" :planRecordId="row.planRecordId" />
                    </template>
                  </template>
                </el-table-column>
              </template>

              <template v-if="isChannelWhatsapp || isChannelPush || isChannelEmail">
                <el-table-column :label="$t('fe.service') | capitalize">
                  <template #default="{ row }">
                    <template v-if="!row.isEmptyCell">
                      <whatsapp-push-tariffs-table-column
                        :isMau="row.isMau"
                        :label="row.label"
                        :planRecordId="row.planRecordId"
                        :channel="channel"
                      />
                    </template>
                  </template>
                </el-table-column>
              </template>

              <el-table-column
                v-if="isM11yOfficeMixin"
                :label="$t('fe.currency') | capitalize"
                width="350"
                prop="currencyId"
              >
                <template #default="{ row }">
                  <template v-if="!row.isEmptyCell">
                    {{ getCurrencyMixin(row.currencyId) }}
                  </template>
                </template>
              </el-table-column>

              <el-table-column :label="$t('fe.quantity') | capitalize" width="350" />
              <el-table-column :label="$t('fe.price') | capitalize" width="338">
                <template #default="{ row }">
                  <template v-if="!row.isEmptyCell">
                    <common-tariffs-table-column
                      isPriceColumn
                      :price="row.price"
                      :steps="row.steps"
                      :planRecordId="row.planRecordId"
                      :currentCurrencySymbol="countryData.currencySymbol"
                    />
                  </template>
                </template>
              </el-table-column>

              <el-table-column type="expand">
                <template #default="{ row }">
                  <el-table class="tariffs__subtable" :data="row.steps" v-if="row.steps && row.steps.length > 1">
                    <template v-if="isChannelSms || isChannelViber">
                      <el-table-column />
                      <el-table-column />
                    </template>
                    <template v-if="isChannelWhatsapp || isChannelPush || isChannelEmail">
                      <el-table-column />
                    </template>
                    <el-table-column width="350">
                      <template #default="{ row }">
                        {{ $t('fe.price.from', [$numberWithSpaces(row.startStep)]) | capitalize }}
                      </template>
                    </el-table-column>
                    <el-table-column width="350">
                      <template #default="{ row }">
                        <template v-if="isM11yOfficeMixin">
                          {{ $ct('fe.price.from', [`${$numberWithSpaces(row.price.toFixed(4))} `]) }}
                        </template>
                        <template v-else>
                          {{
                            $ct('fe.price.from', [
                              `${$numberWithSpaces(row.price.toFixed(4))} ${countryData.currencySymbol}`
                            ])
                          }}
                        </template>
                      </template>
                    </el-table-column>
                  </el-table>
                </template>
              </el-table-column>
            </el-table>
            <tariffs-descriptions
              :channel="channel"
              :isChannelSms="isChannelSms"
              :isChannelViber="isChannelViber"
              :isChannelWhatsapp="isChannelWhatsapp"
            />
          </div>
        </el-tab-pane>
      </el-tabs>
    </template>
    <template v-else-if="showCountrySelection">
      <div>{{ $t('fe.tariffs.needs.select.country') }}</div>
    </template>
  </div>
</template>
